import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Globe } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { SUPPORTED_LANGS, LANG_NAMES } from "../i18n";

export const LanguageSelector = ({ direction = "down", variant = "dropdown" }) => {
  const { lang, switchLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open && listRef.current) {
      const active = listRef.current.querySelector('[aria-selected="true"]');
      (active || listRef.current.querySelector("button"))?.focus();
    }
  }, [open]);

  const onListKeyDown = (e) => {
    const buttons = Array.from(listRef.current?.querySelectorAll("button") || []);
    const idx = buttons.indexOf(document.activeElement);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      buttons[(idx + 1) % buttons.length]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      buttons[(idx - 1 + buttons.length) % buttons.length]?.focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      buttons[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      buttons[buttons.length - 1]?.focus();
    }
  };

  if (variant === "inline") {
    return (
      <div role="group" aria-label={t.a11y.langSelector} className="grid grid-cols-2 gap-2" data-testid="language-selector-inline">
        {SUPPORTED_LANGS.map((l) => (
          <button
            key={l}
            onClick={() => switchLang(l)}
            data-testid={`lang-option-${l}`}
            aria-pressed={l === lang}
            lang={l}
            className={`flex items-center justify-between px-4 py-2.5 text-sm border transition-colors duration-300 ${
              l === lang
                ? "border-[#19C7C1] text-[#19C7C1] bg-[#19C7C1]/10"
                : "border-white/15 text-white/80 hover:border-white/40 hover:text-white"
            }`}
          >
            {LANG_NAMES[l]}
            {l === lang && <Check size={14} aria-hidden="true" />}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="relative" ref={ref} data-testid="language-selector">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t.a11y.langSelector}
        data-testid="language-selector-btn"
        className="inline-flex items-center gap-2 px-3.5 py-2 text-xs font-semibold tracking-widest uppercase text-white/80 hover:text-white border border-white/20 hover:border-[#19C7C1]/60 rounded-full transition-colors duration-300"
      >
        <Globe size={14} aria-hidden="true" />
        {lang.toUpperCase()}
        <ChevronDown size={13} aria-hidden="true" className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul
          ref={listRef}
          role="listbox"
          aria-label={t.a11y.langSelector}
          onKeyDown={onListKeyDown}
          data-testid="language-selector-list"
          className={`absolute right-0 z-[70] w-48 py-2 bg-[#092B35] border border-white/15 shadow-[0_18px_50px_rgba(0,0,0,0.45)] max-h-[min(70vh,26rem)] overflow-y-auto ${
            direction === "up" ? "bottom-full mb-2" : "top-full mt-2"
          }`}
        >
          {SUPPORTED_LANGS.map((l) => (
            <li key={l} role="option" aria-selected={l === lang}>
              <button
                onClick={() => {
                  switchLang(l);
                  setOpen(false);
                }}
                data-testid={`lang-option-${l}`}
                lang={l}
                className={`w-full flex items-center justify-between px-4 py-2.5 text-sm text-left transition-colors duration-200 ${
                  l === lang ? "text-[#19C7C1] bg-[#19C7C1]/10 font-semibold" : "text-white/85 hover:bg-white/5 hover:text-white"
                }`}
              >
                {LANG_NAMES[l]}
                {l === lang && <Check size={14} aria-hidden="true" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
